import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTrackBoardTemplatesCreated, useTrackBoardTemplatesOpened } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { type TabPanel, Tabs } from '@air/primitive-tabs';

import { AGENCY_TEMPLATE } from '~/components/Modals/BoardTemplateModal/agencyTemplate';
import { BoardTemplateModalGenerateTab } from '~/components/Modals/BoardTemplateModal/BoardTemplateModalGenerateTab';
import { BoardTemplateModalTemplatesTab } from '~/components/Modals/BoardTemplateModal/BoardTemplateModalTemplatesTab';
import { countBoards } from '~/components/Modals/BoardTemplateModal/boardTemplateHelpers';
import { MAX_BOARDS_CREATE } from '~/components/Modals/BoardTemplateModal/constants';
import { ECOMMERCE_TEMPLATE } from '~/components/Modals/BoardTemplateModal/ecommerceTemplate';
import { RETAIL_TEMPLATE } from '~/components/Modals/BoardTemplateModal/retailTemplate';
import { type BoardTemplate, type WorkspaceTemplateConfig } from '~/components/Modals/BoardTemplateModal/types';
import { useCreateBoardsMutation } from '~/swr-hooks/boards/useCreateBoardsMutation';

export type BoardTemplateModalProps = {
  workspaceId: string;
  parentBoardId: string;
  onSuccess?: () => void;
};

const WORKSPACE_TEMPLATES: WorkspaceTemplateConfig[] = [AGENCY_TEMPLATE, RETAIL_TEMPLATE, ECOMMERCE_TEMPLATE];

export const BoardTemplateModal = memo(
  ({ workspaceId, parentBoardId, onClose, onSuccess }: AirModalProps<BoardTemplateModalProps>) => {
    const [activeTab, setActiveTab] = useState('templates');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [generatedBoards, setGeneratedBoards] = useState<BoardTemplate[] | null>(null);
    const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
    const { mutate: createBoards, isPending: isCreating } = useCreateBoardsMutation();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { trackBoardTemplatesOpened } = useTrackBoardTemplatesOpened();
    const { trackBoardTemplatesCreated } = useTrackBoardTemplatesCreated();

    useEffect(() => {
      trackBoardTemplatesOpened({ type: 'board' });
    }, [trackBoardTemplatesOpened]);

    const getBoardsToCreate = useCallback((): BoardTemplate[] | null => {
      if (activeTab === 'templates' && selectedTemplateId) {
        const template = WORKSPACE_TEMPLATES.find((t) => t.id === selectedTemplateId);
        return template?.boards || null;
      }
      if (activeTab === 'generate' && generatedBoards) {
        return generatedBoards;
      }
      return null;
    }, [activeTab, selectedTemplateId, generatedBoards]);

    const handleCreate = useCallback(() => {
      const boardsToCreate = getBoardsToCreate();

      if (!boardsToCreate) return;

      const boardCount = countBoards(boardsToCreate);
      const prompt = activeTab === 'templates' ? selectedTemplateId || '' : generatedPrompt;

      createBoards(
        {
          workspaceId,
          boards: boardsToCreate,
          parentId: parentBoardId,
        },
        {
          onSuccess: () => {
            trackBoardTemplatesCreated({ prompt, number_of_boards: boardCount });
            onSuccess?.();
            onClose();
          },
        },
      );
    }, [
      workspaceId,
      parentBoardId,
      createBoards,
      onSuccess,
      onClose,
      getBoardsToCreate,
      activeTab,
      selectedTemplateId,
      generatedPrompt,
      trackBoardTemplatesCreated,
    ]);

    const handleBoardsGenerated = useCallback((boards: BoardTemplate[], prompt: string) => {
      setGeneratedBoards(boards);
      setGeneratedPrompt(prompt);
    }, []);

    const handleReset = useCallback(() => {
      setGeneratedBoards(null);
      textareaRef.current?.focus();
    }, []);

    const handleTabChange = useCallback((value: string) => {
      if (value === 'templates' || value === 'generate') {
        setActiveTab(value);
      }
    }, []);

    const boardsToCreate = useMemo(() => getBoardsToCreate(), [getBoardsToCreate]);
    const boardCount = useMemo(() => (boardsToCreate ? countBoards(boardsToCreate) : 0), [boardsToCreate]);
    const exceedsLimit = boardCount > MAX_BOARDS_CREATE;
    const canCreate = boardsToCreate !== null && !exceedsLimit;

    const tabPanels = useMemo<TabPanel[]>(
      () => [
        {
          id: 'templates',
          label: 'Templates',
          content: (
            <BoardTemplateModalTemplatesTab
              selectedTemplateId={selectedTemplateId}
              onSelectTemplate={setSelectedTemplateId}
              templates={WORKSPACE_TEMPLATES}
            />
          ),
        },
        {
          id: 'generate',
          label: 'Generate',
          content: (
            <BoardTemplateModalGenerateTab
              workspaceId={workspaceId}
              isCreating={isCreating}
              generatedBoards={generatedBoards}
              onBoardsGenerated={handleBoardsGenerated}
              textareaRef={textareaRef}
            />
          ),
        },
      ],
      [selectedTemplateId, generatedBoards, workspaceId, isCreating, handleBoardsGenerated],
    );

    return (
      <Modal isOpen onDismiss={onClose} size="large">
        <header className="mb-3 flex items-center justify-between">
          <ModalTitle>Board Templates</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </header>

        <Tabs value={activeTab} onValueChange={handleTabChange} panels={tabPanels} />

        <div className="-mx-6 mt-6 -mb-6 flex justify-between px-6 py-4">
          <div>
            {activeTab === 'generate' && generatedBoards && (
              <Button onClick={handleReset} disabled={isCreating} appearance="ghost" color="grey" size="large">
                Try Again
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button onClick={onClose} disabled={isCreating} appearance="ghost" color="grey" size="large">
              Cancel
            </Button>
            {canCreate && (
              <Button
                onClick={handleCreate}
                disabled={isCreating}
                isLoading={isCreating}
                appearance="filled"
                color="blue"
                size="large"
              >
                Create Boards
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  },
);

BoardTemplateModal.displayName = 'BoardTemplateModal';
